import { ReactComponent as ShieldExclamationIcon } from '../../../../assets/shield_exclamation.svg';
import { Obligation } from '../../../../types/Report.types';
import { formatSourceElement } from '../../../../utils/Functions.utils';
import Banner from '../../../ui/Banner/Banner';
import { ReactComponent as WarningIcon } from '../../../../assets/warning-icon.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/error-icon.svg';
import { ReactNode, useMemo } from 'react';
import { Briefcase } from '../../../../icons/SvgIcons';

interface CustomObligationBannerProps {
  obligation: Obligation;
}

type BannerConfig = {
  background: string;
  title: string;
  text: string;
  icon: ReactNode;
};

export function CustomObligationBanner({
  obligation
}: CustomObligationBannerProps) {
  const formatedLabel = formatSourceElement(obligation.label, 'underline');

  const RISK_OBLIGATION_STYLE: Record<string, BannerConfig> = {
    'high-risk': {
      background: 'bg-orange-50',
      title: 'text-orange-700',
      text: 'text-orange-900',
      icon: <WarningIcon height={15} width={15} />
    },
    prohibited: {
      background: 'bg-rose-50',
      title: 'text-rose-700',
      text: 'text-rose-900',
      icon: <ErrorIcon height={15} width={15} />
    }
  };

  const ENTITY_OBLIGATION = new Set([
    'Provider obligations',
    'Deployer obligations',
    'Distributor obligations',
    'Importer obligations',
    'Product manufacturer obligations',
    'Authorised representative obligations'
  ]);

  const ENTITY_OBLIGATION_STYLE: BannerConfig = {
    background: 'bg-cyan-150',
    title: 'text-cyan-750',
    text: 'text-[#164E63]',
    icon: <Briefcase />
  };

  const DEFAULT_OBLIGATION_STYLE: BannerConfig = {
    background: 'bg-gray-40',
    title: 'text-gray-800',
    text: 'text-gray-900v2',
    icon: <ShieldExclamationIcon width="17" height="17" />
  };

  const bannerStyle = useMemo(() => {
    const normalizedRiskName = obligation.name.toLowerCase().replace(/_/g, '-');

    if (RISK_OBLIGATION_STYLE[normalizedRiskName])
      return RISK_OBLIGATION_STYLE[normalizedRiskName];

    if (ENTITY_OBLIGATION.has(obligation.name)) return ENTITY_OBLIGATION_STYLE;

    return DEFAULT_OBLIGATION_STYLE;
  }, [obligation.name]);

  return (
    <Banner
      icon={bannerStyle.icon}
      title={
        <span className="text-base tracking-normal">{obligation.name}</span>
      }
      text={
        <div className="flex flex-col gap-2 text-base font-normal tracking-normal whitespace-pre-line">
          {obligation.labelHtml ? (
            <div dangerouslySetInnerHTML={{ __html: obligation.labelHtml }} />
          ) : (
            <div dangerouslySetInnerHTML={{ __html: formatedLabel }} />
          )}
        </div>
      }
      backgroundColorClass={bannerStyle.background}
      titleColorClass={bannerStyle.title}
      textColorClass={bannerStyle.text}
    />
  );
}
